<template>
    <div>
        <b-container fluid>
            <h3 class="mt-3 mb-3">Auto Route Assignment</h3>

            <h5 class="mt-4">Global Settings</h5>
            <p class="text-muted" style="font-size: 13px;">
                Turn off to stop the "Route Auto-Assigned — Please Review"
                popup from appearing for everyone, on every client. Routes are
                still auto-assigned and learned in the background either way —
                this only silences the review popup.
            </p>
            <b-form-checkbox
                switch
                :checked="popup_enabled == 1"
                :disabled="!canEdit"
                @change="onTogglePopup"
            >Show "Route Auto-Assigned — Please Review" popup</b-form-checkbox>

            <h5 class="mt-4">Client Control</h5>
            <p class="text-muted" style="font-size: 13px;">
                Turn on to let the system auto-assign a route (via saved
                address history, or Google Geocoding as a fallback) whenever
                staff leave the route blank for this client.
            </p>
            <b-table
                responsive
                small
                outlined
                striped
                hover
                sticky-header="300px"
                :items="clients"
                :fields="client_fields"
            >
                <template v-slot:cell(enable_auto_route_assign)="data">
                    <b-form-checkbox
                        switch
                        :checked="data.item.enable_auto_route_assign == 1"
                        :disabled="!canEdit"
                        @change="onToggleClient(data.item)"
                    />
                </template>
            </b-table>

            <h5 class="mt-4">Address &harr; Route History</h5>
            <p class="text-muted" style="font-size: 13px;">
                Look up a specific order, address, or everything currently on
                a route (handy when a route gets re-allocated and existing
                records need to move to the new route too) to review or
                correct it. The full history is too large to browse — enter
                an order #, address, or route to search.
            </p>

            <div class="row mb-2">
                <div class="col-2">
                    <b-form-input
                        size="sm"
                        type="text"
                        v-model="search_criteria.order_number"
                        placeholder="Order #"
                        @keyup.enter="onSearch"
                    />
                </div>
                <div class="col-3">
                    <b-form-input
                        size="sm"
                        type="text"
                        v-model="search_criteria.address"
                        placeholder="Search address (freetext)"
                        @keyup.enter="onSearch"
                    />
                </div>
                <div class="col-2">
                    <b-form-select
                        size="sm"
                        v-model="search_criteria.route"
                        :options="route_filter_options"
                    />
                </div>
                <div class="col-2">
                    <b-form-select
                        size="sm"
                        v-model="search_criteria.source"
                        :options="source_filter_options"
                    />
                </div>
                <div class="col-2">
                    <b-form-select
                        size="sm"
                        v-model="search_criteria.reliability"
                        :options="reliability_filter_options"
                    />
                </div>
                <div class="col-1">
                    <b-button
                        size="sm"
                        variant="info"
                        :disabled="!canSearch"
                        @click="onSearch"
                        >Search</b-button
                    >
                </div>
            </div>

            <b-alert v-if="!has_searched" show variant="light">
                Enter an order #, address, or route above and click Search.
            </b-alert>

            <template v-else>
                <div class="summary mb-3" v-if="summary">
                    <b-badge variant="secondary" class="mr-2"
                        >Total: {{ summary.total || 0 }}</b-badge
                    >
                    <b-badge variant="info" class="mr-2"
                        >Google-assigned: {{ summary.google_assigned || 0 }}</b-badge
                    >
                    <b-badge variant="success" class="mr-2"
                        >Staff-corrected: {{ summary.staff_corrected || 0 }}</b-badge
                    >
                    <b-badge variant="warning"
                        >Low confidence: {{ summary.low_confidence || 0 }}</b-badge
                    >
                </div>

                <div class="row mb-2 align-items-center" v-if="canEdit && rows.length">
                    <div class="col-auto">
                        <b-form-select
                            size="sm"
                            v-model="bulk_route"
                            :options="tracking_route_options"
                            :disabled="!selected_count"
                        />
                    </div>
                    <div class="col-auto">
                        <b-button
                            size="sm"
                            variant="warning"
                            :disabled="!selected_count || !bulk_route"
                            @click="onOpenBulkEdit"
                            >Bulk Edit Route ({{ selected_count }} selected)</b-button
                        >
                    </div>
                    <div class="col-auto" v-if="select_all_matching">
                        <b-button
                            size="sm"
                            variant="link"
                            @click="clearSelection"
                            >Clear selection</b-button
                        >
                    </div>
                </div>

                <div
                    class="mb-2 small"
                    v-if="canEdit && !select_all_matching && total_rows > rows.length"
                >
                    Showing {{ rows.length }} of {{ total_rows }} matching addresses.
                    <b-button
                        size="sm"
                        variant="link"
                        class="p-0 align-baseline"
                        :disabled="selecting_all"
                        @click="onSelectAllMatching"
                        >Select all {{ total_rows }} matching addresses</b-button
                    >
                </div>
                <div class="mb-2 small" v-if="canEdit && select_all_matching">
                    All {{ selected_count }} matching addresses are selected.
                </div>

                <Pagination
                    :totalRows="total_rows"
                    :perPage="rows_per_page"
                    :onPageChange="onPageChange"
                    :pagingLimit="paging_limit"
                    :currentPage="currentPage"
                    :onPerPageChange="onPerPageChange"
                />

                <b-table
                    responsive
                    small
                    outlined
                    striped
                    hover
                    sticky-header="500px"
                    :items="rows"
                    :fields="row_fields"
                >
                <template v-slot:head(chk_box)="data">
                    <b-form-checkbox
                        v-if="canEdit"
                        size="sm"
                        plain
                        v-model="check_all_rows"
                    />
                </template>
                <template v-slot:cell(chk_box)="data">
                    <b-form-checkbox
                        v-if="canEdit"
                        size="sm"
                        plain
                        v-model="data.item.is_checked"
                    />
                </template>

                <template v-slot:cell(source)="data">
                    <b-badge
                        :variant="data.item.source === 'staff_corrected' ? 'success' : 'info'"
                        >{{ data.item.source }}</b-badge
                    >
                </template>

                <template v-slot:cell(reliability)="data">
                    <b-badge
                        :variant="
                            data.item.reliability === 'HIGH'
                                ? 'success'
                                : data.item.reliability === 'LOW'
                                ? 'danger'
                                : 'warning'
                        "
                        >{{ data.item.reliability || '-' }}</b-badge
                    >
                </template>

                <template v-slot:cell(verified)="data">
                    {{ data.item.verified == 1 ? 'Yes' : 'No' }}
                </template>

                <template v-slot:cell(route)="data">
                    <div v-if="editing_id === data.item.id" class="d-flex">
                        <b-form-select
                            size="sm"
                            v-model="editing_route"
                            :options="tracking_route_options"
                        />
                        <b-button
                            size="sm"
                            variant="info"
                            class="ml-1"
                            @click="onSaveCorrection(data.item)"
                            >Save</b-button
                        >
                        <b-button
                            size="sm"
                            variant="outline-secondary"
                            class="ml-1"
                            @click="editing_id = null"
                            >Cancel</b-button
                        >
                    </div>
                    <div v-else>
                        {{ data.item.route }}
                        <b-button
                            v-if="canEdit"
                            size="sm"
                            variant="link"
                            @click="onStartEdit(data.item)"
                            >Correct</b-button
                        >
                    </div>
                </template>
            </b-table>

                <Pagination
                    :totalRows="total_rows"
                    :perPage="rows_per_page"
                    :onPageChange="onPageChange"
                    :pagingLimit="paging_limit"
                    :currentPage="currentPage"
                    :onPerPageChange="onPerPageChange"
                />
            </template>
        </b-container>

        <b-modal
            id="bulk-route-edit-modal"
            title="Bulk Edit Route"
            :hide-footer="true"
            @hide="bulk_saving = false"
        >
            <p>
                Move <strong>{{ selected_count }}</strong> address(es) to
                route <strong>{{ bulk_route }}</strong>. This updates the
                route on every existing order still using these addresses, as
                well as how the system routes them going forward.
            </p>
            <b-form-group label="Selected Addresses" v-if="!select_all_matching">
                <ul>
                    <li v-for="row in checked_rows" :key="row.id">
                        {{ row.address }} <span class="text-muted">(was {{ row.route }})</span>
                    </li>
                </ul>
            </b-form-group>

            <div class="d-flex justify-content-end">
                <b-button
                    variant="secondary"
                    :disabled="bulk_saving"
                    @click="$bvModal.hide('bulk-route-edit-modal')"
                    >Cancel</b-button
                >
                <b-button
                    variant="primary"
                    class="ml-2"
                    :disabled="bulk_saving"
                    @click="onConfirmBulkEdit"
                    >Confirm</b-button
                >
            </div>
        </b-modal>
    </div>
</template>

<script>
import Pagination from "./common/Pagination";
import apiService from "../services/ApiService";
import global_function from "../mixins/global_function";

const client_fields = [
    { key: "client_name", label: "Client" },
    { key: "enable_auto_route_assign", label: "Auto Route Assignment" }
];

const row_fields = [
    { key: "chk_box", label: "" },
    { key: "address", label: "Address" },
    { key: "route", label: "Route" },
    { key: "source", label: "Source" },
    { key: "reliability", label: "Reliability" },
    { key: "verified", label: "Staff Verified" },
    { key: "updated_at", label: "Last Updated" }
];

export default {
    name: "AutoRouteAssignment",
    inject: [
        "API_HOST",
        "TABLE_LIMIT",
        "PAGING_LIMIT",
        "DEFAULT_ROWS_PER_PAGE"
    ],
    mixins: [apiService, global_function],
    components: {
        Pagination
    },
    data() {
        return {
            loader: false,
            popup_enabled: 1,
            clients: [],
            client_fields: client_fields,
            rows: [],
            row_fields: row_fields,
            summary: null,
            tracking_route_options: [],
            currentPage: 1,
            total_rows: 0,
            rows_per_page: this.DEFAULT_ROWS_PER_PAGE,
            paging_limit: this.PAGING_LIMIT,
            search_criteria: {
                order_number: "",
                address: "",
                route: "",
                source: "",
                reliability: "",
                verified: ""
            },
            has_searched: false,
            select_all_matching: false,
            all_matching_ids: [],
            selecting_all: false,
            route_filter_options: [{ value: "", text: "Current Route: All" }],
            source_filter_options: [
                { value: "", text: "All Sources" },
                { value: "google", text: "Google" },
                { value: "staff_corrected", text: "Staff Corrected" }
            ],
            reliability_filter_options: [
                { value: "", text: "All Reliability" },
                { value: "HIGH", text: "HIGH" },
                { value: "MEDIUM", text: "MEDIUM" },
                { value: "LOW", text: "LOW" }
            ],
            verified_filter_options: [
                { value: "", text: "All" },
                { value: "1", text: "Verified" },
                { value: "0", text: "Unverified" }
            ],
            editing_id: null,
            editing_route: null,
            check_all_rows: false,
            bulk_route: null,
            bulk_saving: false
        };
    },
    computed: {
        table_offset: function() {
            return (this.currentPage - 1) * this.rows_per_page;
        },
        canEdit: function() {
            return this.checkPermission(
                this.$store.state.user_obj,
                "Delivery Management",
                "Auto Route Assignment",
                "Edit",
                true
            );
        },
        canSearch: function() {
            return !!(
                this.search_criteria.order_number ||
                this.search_criteria.address ||
                this.search_criteria.route
            );
        },
        checked_rows: function() {
            return this.rows.filter(row => row.is_checked);
        },
        selected_count: function() {
            return this.select_all_matching
                ? this.all_matching_ids.length
                : this.checked_rows.length;
        }
    },
    watch: {
        check_all_rows(new_value) {
            this.rows.forEach(row => {
                this.$set(row, "is_checked", !!new_value);
            });
        },
        rows() {
            this.check_all_rows = false;
            this.select_all_matching = false;
        },
        "checked_rows.length"(count) {
            if (this.select_all_matching && count < this.rows.length) {
                this.select_all_matching = false;
            }
        }
    },
    async created() {
        if (
            !this.checkPermission(
                this.$store.state.user_obj,
                "Delivery Management",
                "Auto Route Assignment",
                "View",
                true
            )
        ) {
            this.$router.replace("/");
            return;
        }

        let routes_response = await this.getTrackingRoutes();
        for (let i in routes_response.tracking_routes) {
            this.tracking_route_options.push({
                value: routes_response.tracking_routes[i].route_name,
                text: routes_response.tracking_routes[i].route_name
            });
            this.route_filter_options.push({
                value: routes_response.tracking_routes[i].route_name,
                text: routes_response.tracking_routes[i].route_name
            });
        }

        await this.getList();
    },
    mounted() {
        this.loader = this.$loading.show({
            isFullPage: true,
            canCancel: false
        });
    },
    methods: {
        async getList() {
            let response = await this.$http({
                method: "POST",
                baseURL: `${this.API_HOST}`,
                url: "address_route_list_get.php",
                data: {
                    page: this.currentPage,
                    limit: this.rows_per_page,
                    search_criteria: this.search_criteria,
                    user_id: this.$store.state.user_obj.user_id
                }
            });

            this.clients = response.data.clients;
            this.popup_enabled = response.data.popup_enabled;
            this.rows = response.data.rows.map(row => ({
                ...row,
                is_checked: false
            }));
            this.summary = response.data.summary;
            this.total_rows = response.data.rows.length
                ? parseInt(response.data.summary.total)
                : 0;

            if (this.loader) {
                this.loader.hide();
            }
        },

        async onSelectAllMatching() {
            this.selecting_all = true;
            try {
                let response = await this.$http({
                    method: "POST",
                    baseURL: `${this.API_HOST}`,
                    url: "address_route_list_get.php",
                    data: {
                        ids_only: true,
                        search_criteria: this.search_criteria,
                        user_id: this.$store.state.user_obj.user_id
                    }
                });
                this.all_matching_ids = response.data.ids || [];
                this.select_all_matching = true;
            } finally {
                this.selecting_all = false;
            }
        },

        clearSelection() {
            this.select_all_matching = false;
            this.check_all_rows = false;
            this.rows.forEach(row => this.$set(row, "is_checked", false));
        },

        onSearch() {
            if (!this.canSearch) {
                return;
            }
            this.has_searched = true;
            this.currentPage = 1;
            this.getList();
        },

        onPageChange(page_to_change) {
            this.currentPage = page_to_change;
            this.getList();
        },

        onPerPageChange(_perPage) {
            this.currentPage = 1;
            this.rows_per_page = _perPage;
            this.getList();
        },

        async onToggleClient(client_item) {
            let new_value = client_item.enable_auto_route_assign == 1 ? 0 : 1;

            await this.$http({
                method: "POST",
                baseURL: `${this.API_HOST}`,
                url: "client_auto_route_toggle.php",
                data: {
                    client_id: client_item.id,
                    enabled: new_value,
                    user_id: this.$store.state.user_obj.user_id
                }
            });

            client_item.enable_auto_route_assign = new_value;
        },

        async onTogglePopup() {
            let new_value = this.popup_enabled == 1 ? 0 : 1;

            await this.$http({
                method: "POST",
                baseURL: `${this.API_HOST}`,
                url: "auto_route_popup_toggle.php",
                data: {
                    enabled: new_value,
                    user_id: this.$store.state.user_obj.user_id
                }
            });

            this.popup_enabled = new_value;
        },

        onStartEdit(row_item) {
            this.editing_id = row_item.id;
            this.editing_route = row_item.route;
        },

        async onSaveCorrection(row_item) {
            if (!this.editing_route || this.editing_route === row_item.route) {
                this.editing_id = null;
                return;
            }

            await this.$http({
                method: "POST",
                baseURL: `${this.API_HOST}`,
                url: "address_route_correct.php",
                data: {
                    id: row_item.id,
                    route: this.editing_route,
                    user_id: this.$store.state.user_obj.user_id
                }
            });

            this.editing_id = null;
            await this.getList();
        },

        onOpenBulkEdit() {
            if (!this.selected_count || !this.bulk_route) {
                return;
            }
            this.$bvModal.show("bulk-route-edit-modal");
        },

        async onConfirmBulkEdit() {
            if (this.bulk_saving) {
                return;
            }
            this.bulk_saving = true;

            await this.$http({
                method: "POST",
                baseURL: `${this.API_HOST}`,
                url: "address_route_bulk_correct.php",
                data: {
                    ids: this.select_all_matching
                        ? this.all_matching_ids
                        : this.checked_rows.map(row => row.id),
                    route: this.bulk_route,
                    user_id: this.$store.state.user_obj.user_id
                }
            });

            this.bulk_saving = false;
            this.bulk_route = null;
            this.$bvModal.hide("bulk-route-edit-modal");
            await this.getList();
        }
    }
};
</script>

<style>
th,
td {
    white-space: nowrap;
}
</style>



// WEBPACK FOOTER //
// src/components/AutoRouteAssignment.vue